import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGSAP } from '@/composables/useGSAP'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import { useRunnerState } from '@/composables/useRunnerState'
import { getContentfulUrl } from '@/utils/contentful'
import useMedia from '@/utils/hooks/useMedia'

export default function RunnerPersona() {
  const { t } = useTranslation()
  const { gsap } = useGSAP()
  const isReducedMotion = useMedia('(prefers-reduced-motion: reduce)')

  const { persona, context, gameState } = useRunnerState()

  const id = useId()
  const pathRef = useRef<SVGPathElement>(null)
  const mainPathRef = useRef<SVGPathElement>(null)
  const [subTimeline, setSubTimeline] = useState<gsap.core.Timeline | null>(null)
  const [mainTimeline, setMainTimeline] = useState<gsap.core.Timeline | null>(null)
  const [isPaused, setIsPaused] = useState(false)

  const openPath =
    'M111.78 32.8835C114.211 35.5391 114.211 39.4833 111.78 42.1389C103.99 50.647 85.5313 67.9112 64.0001 67.9112C42.4688 67.9112 24.0097 50.647 16.2201 42.1389C13.7887 39.4833 13.7887 35.5391 16.2201 32.8835C24.0097 24.3754 42.4688 7.11121 64.0001 7.11121C85.5313 7.11121 103.99 24.3754 111.78 32.8835Z'
  const middlePath =
    'M107.41 31.9612C111.83 34.5711 111.83 40.4511 107.41 43.061C98.1858 48.5068 82.1891 56 64.0001 56C45.811 56 29.8144 48.5068 20.5903 43.061C16.1696 40.4511 16.1696 34.5711 20.5903 31.9612C29.8144 26.5154 45.811 19.0222 64.0001 19.0222C82.1891 19.0222 98.1858 26.5154 107.41 31.9612Z'
  const closePath =
    'M111.206 37.4229C111.337 37.4252 111.337 37.5967 111.206 37.599C103.11 37.7446 85.0192 38.02 64.0001 38.02C42.981 38.02 24.8896 37.7446 16.794 37.599C16.6636 37.5967 16.6636 37.4252 16.794 37.4229C24.8896 37.2766 42.981 37 64.0001 37C85.0192 37 103.11 37.2766 111.206 37.4229Z'

  const currentLane = context.game.currentLane || 0

  useLayoutEffect(() => {
    if (isReducedMotion) {
      setSubTimeline(null)
      return
    }
    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({ repeat: -1 })
      timeline.fromTo(
        pathRef.current,
        {
          morphSVG: closePath,
        },
        {
          morphSVG: openPath,
          duration: 0.7,
          ease: 'power4.out',
        }
      )
      timeline.to(
        pathRef.current,
        {
          morphSVG: middlePath,
          duration: 0.3,
          ease: 'power4.out',
        },
        '+=0.6'
      )
      timeline.to(pathRef.current, {
        morphSVG: openPath,
        duration: 0.7,
        ease: 'power4.out',
      })
      timeline.to(
        pathRef.current,
        {
          morphSVG: closePath,
          duration: 0.2,
          ease: 'power4.out',
        },
        '+=1'
      )
      timeline.to(pathRef.current, {
        morphSVG: middlePath,
        duration: 0.3,
        ease: 'power4.out',
      })
      timeline.to(pathRef.current, {
        morphSVG: closePath,
        duration: 0.2,
        ease: 'power4.out',
      })

      setSubTimeline(timeline)
    })

    return () => {
      ctx?.revert()
    }
  }, [gsap, pathRef, setSubTimeline, isReducedMotion])

  useLayoutEffect(() => {
    if (isReducedMotion) {
      setMainTimeline(null)
      return
    }
    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({ paused: true })

      timeline.fromTo(
        mainPathRef.current,
        {
          morphSVG: openPath,
        },
        {
          morphSVG: closePath,
          duration: 0.2,
          ease: 'power4.out',
        }
      )
      timeline.to(mainPathRef.current, {
        morphSVG: openPath,
        duration: 0.1,
        ease: 'power4.in',
      })

      setMainTimeline(timeline)
    })

    return () => {
      ctx?.revert()
    }
  }, [gsap, mainPathRef, setMainTimeline, isReducedMotion])

  useLayoutEffect(() => {
    if (isReducedMotion || !mainTimeline || !subTimeline) return
    const ctx = gsap.context(() => {
      const timeline = gsap.timeline()

      timeline.add(() => {
        mainTimeline.restart()
      })
      timeline.add(() => {
        subTimeline.progress(0)
        subTimeline.pause()
      }, '+=0.2')
      timeline.add(() => {
        subTimeline.restart()
      }, '+=0.1')
    })
    return () => {
      ctx?.revert()
    }
  }, [currentLane, mainTimeline, subTimeline, gsap, isReducedMotion])

  useEffect(() => {
    const stopAnims = gameState.paused || !!gameState.popin
    // Stop animations of the small eye when the game is paused (or popins opens)
    if (isReducedMotion || !subTimeline || isPaused === stopAnims) return
    setIsPaused(stopAnims)
    if (stopAnims) {
      subTimeline.pause()
    } else {
      subTimeline.resume()
    }
  }, [gameState, isPaused, subTimeline, isReducedMotion])

  const betterFormat = useImageFormatContext()
  const [imageUrl, setImageUrl] = useState(null)
  useEffect(() => {
    const url = getContentfulUrl(`/src/assets/images/chat/${persona}.png`, { w: 256, fm: betterFormat })
    setImageUrl(url)
  }, [persona, betterFormat])

  return (
    <div className='RunnerPersona__Wrapper absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -mt-20'>
      <div
        className='RunnerPersona relative w-128 __bottom-176 __md:bottom-120 top-[30vw] landscape:top-[30vh]'
        style={{ clipPath: `url('#clip-main-${id}')` }}
      >
        <div className='RunnerPersona__Inner w-full relative' style={{ clipPath: `url('#clip-${id}')` }}>
          <svg
            className='block w-full text-tertiary'
            viewBox='0 0 128 76'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <defs>
              <clipPath id={`clip-${id}`}>
                <path
                  id={`path-${id}`}
                  ref={pathRef}
                  d='M111.78 32.8835C114.211 35.5391 114.211 39.4833 111.78 42.1389C103.99 50.647 85.5313 67.9112 64.0001 67.9112C42.4688 67.9112 24.0097 50.647 16.2201 42.1389C13.7887 39.4833 13.7887 35.5391 16.2201 32.8835C24.0097 24.3754 42.4688 7.11121 64.0001 7.11121C85.5313 7.11121 103.99 24.3754 111.78 32.8835Z'
                />
              </clipPath>
              <clipPath id={`clip-main-${id}`}>
                <path
                  id={`path-main-${id}`}
                  ref={mainPathRef}
                  d='M111.78 32.8835C114.211 35.5391 114.211 39.4833 111.78 42.1389C103.99 50.647 85.5313 67.9112 64.0001 67.9112C42.4688 67.9112 24.0097 50.647 16.2201 42.1389C13.7887 39.4833 13.7887 35.5391 16.2201 32.8835C24.0097 24.3754 42.4688 7.11121 64.0001 7.11121C85.5313 7.11121 103.99 24.3754 111.78 32.8835Z'
                />
              </clipPath>
            </defs>

            <use href={`#path-${id}`} fill='currentColor' />
          </svg>

          <img
            className='block w-52 h-52 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
            src={imageUrl}
            alt={t(`child.${persona}.name`)}
          />
        </div>
      </div>
    </div>
  )
}
